import { ticketUpperCategories } from "@agentcall/core/master"
import { ticketType } from "@agentcall/core/type"
import * as React from "react"
import { Minus, Plus, RotateCcw, Search } from "react-feather"
import { propertyApi } from "@/api/client/property"
import { Button } from "@/components/ui/button/button"
import { Card } from "@/components/ui/card/card"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { Input } from "@/components/ui/input/input"
import { Label } from "@/components/ui/label/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"
import { cn } from "@/lib/utils"

export interface TicketFilterValues {
  keyword?: string
  property?: string
  category?: string
  assignee?: string
  status?: string
  showOnlyUnread?: boolean
  showCompleted?: boolean
}

interface TicketSearchFilterProps {
  searchValue?: string
  onSearchChange?: (value: string) => void
  onSearch?: () => void
  filters: TicketFilterValues
  onFiltersApply?: (values: TicketFilterValues) => void
  className?: string
  defaultOpen?: boolean
}

export const TicketSearchFilter = React.forwardRef<HTMLDivElement, TicketSearchFilterProps>(
  (
    {
      searchValue,
      onSearchChange,
      onSearch,
      filters,
      onFiltersApply,
      className,
      defaultOpen = false,
    },
    ref
  ) => {
    const [isFilterOpen, setIsFilterOpen] = React.useState(defaultOpen)
    // ローカルフィルター状態（詳細フィルター用）
    const [localFilters, setLocalFilters] = React.useState<TicketFilterValues>(filters)
    // ローカル検索入力状態
    const [localSearchValue, setLocalSearchValue] = React.useState(searchValue || "")

    // 物件一覧を取得
    const { data: propertyData } = propertyApi.usePropertyList({ limit: 1000 })

    // 外部からfiltersが変更された場合にローカル状態を同期
    React.useEffect(() => {
      setLocalFilters(filters)
    }, [filters])

    // 外部からsearchValueが変更された場合にローカル状態を同期
    React.useEffect(() => {
      setLocalSearchValue(searchValue || "")
    }, [searchValue])

    const handleToggleFilters = () => {
      if (!onFiltersApply) return
      setIsFilterOpen((prev) => !prev)
    }

    const handleSearch = () => {
      // 検索ボタン押下時にフィルターを適用
      onSearchChange?.(localSearchValue)
      onSearch?.()
      onFiltersApply?.({ ...localFilters, keyword: localSearchValue })
    }

    const handleFilterChange = <K extends keyof TicketFilterValues>(
      key: K,
      value: TicketFilterValues[K]
    ) => {
      setLocalFilters((prev) => ({ ...prev, [key]: value }))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSearch()
      }
    }

    const handleReset = () => {
      const emptyFilters: TicketFilterValues = {}
      setLocalFilters(emptyFilters)
      setLocalSearchValue("")
      onSearchChange?.("")
      onFiltersApply?.(emptyFilters)
    }

    return (
      <Card ref={ref} className={cn("p-4", className)}>
        <div className="flex flex-col gap-2.5">
          <Input
            placeholder="案件を検索"
            value={localSearchValue}
            onChange={(e) => setLocalSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            startIcon={<Search className="w-4 h-4" />}
            className="h-10"
          />
          {isFilterOpen && (
            <>
              {/* 物件 */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-600">物件</Label>
                <Select
                  value={localFilters.property || "all"}
                  onValueChange={(value) =>
                    handleFilterChange("property", value === "all" ? undefined : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全て" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全て</SelectItem>
                    {propertyData?.properties.map((property) => (
                      <SelectItem key={property.id} value={String(property.id)}>
                        {property.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 大カテゴリー */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-600">大カテゴリー</Label>
                <Select
                  value={localFilters.category || "all"}
                  onValueChange={(value) =>
                    handleFilterChange("category", value === "all" ? undefined : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全て" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全て</SelectItem>
                    {ticketUpperCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 手配先業者 */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-600">手配先業者</Label>
                <Select
                  value={localFilters.assignee || "all"}
                  onValueChange={(value) =>
                    handleFilterChange("assignee", value === "all" ? undefined : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全て" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全て</SelectItem>
                    {ticketType.CONTRACTOR_TYPES.map((type) => (
                      <SelectItem key={type.key} value={type.key}>
                        {type.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* 業者手配状況 */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-600">業者手配状況</Label>
                <Select
                  value={localFilters.status || "all"}
                  onValueChange={(value) =>
                    handleFilterChange("status", value === "all" ? undefined : value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="全て" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全て</SelectItem>
                    {ticketType.CONTRACTOR_STATUSES.map((status) => (
                      <SelectItem key={status.key} value={status.key}>
                        {status.value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* チェックボックスオプション */}
              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="unread"
                    checked={localFilters.showOnlyUnread || false}
                    onCheckedChange={(checked) =>
                      handleFilterChange("showOnlyUnread", checked === true)
                    }
                  />
                  <Label
                    htmlFor="unread"
                    className="text-sm font-medium text-slate-600 cursor-pointer"
                  >
                    未読のみ表示
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="completed"
                    checked={localFilters.showCompleted === false}
                    onCheckedChange={(checked) =>
                      handleFilterChange("showCompleted", checked === true ? false : undefined)
                    }
                  />
                  <Label
                    htmlFor="completed"
                    className="text-sm font-medium text-slate-600 cursor-pointer"
                  >
                    完了を除く
                  </Label>
                </div>
              </div>
            </>
          )}
          <div className="flex flex-col gap-5">
            <Button
              variant="outline"
              className="flex-1 h-10 border-slate-400 text-[#354267]"
              onClick={handleToggleFilters}
              disabled={!onFiltersApply}
            >
              {isFilterOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              {isFilterOpen ? "表示を隠す" : "条件を絞り込む"}
            </Button>
            <div className="flex gap-2">
              <Button
                className="flex-1 h-10 bg-[#354267] hover:bg-[#354267]/90"
                onClick={handleSearch}
              >
                <Search className="w-3.5 h-3.5 mr-2" />
                検索
              </Button>
              <Button
                variant="outline"
                className="h-10 border-slate-400 text-[#354267]"
                onClick={handleReset}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-2" />
                リセット
              </Button>
            </div>
          </div>
        </div>
      </Card>
    )
  }
)

TicketSearchFilter.displayName = "TicketSearchFilter"
